import { ErrorHandler, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";
import { IstrapiRole, entryProp, indexDataField } from "../interfaces/apiCalls";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DateValidator, PasswordValidator, RoleValidator, BoolValidator, MatriculeValidator } from "../utils/validators.validator";
import { StateService } from "../@core/utils";
import dayjs from 'dayjs'
import axios, { AxiosError } from 'axios';
import { NbComponentStatus, NbGlobalPhysicalPosition, NbToastrConfig } from "@nebular/theme";
import { DocKind } from "../shared-components/eCIT/e-cit.types";

/*
* Just wait for a given amount of milliseconds - use for debugging etc.
*/
export const wait = (ms: number) =>{
  return new Promise<void>((resolve, reject) => {
    setTimeout(() => {
      resolve();
    }, ms);
  })}

/*
* Replacement for the somehow buggy .includes in Chrome
*/
export function exists(arr, search) {
  return arr.some(row =>
      Array.isArray(row) &&
      Array.isArray(search) &&
      row.length === search.length &&
      row.every((val, index) => val === search[index])
)
};

/*
* for typecasting in template to satisfy TS nonsense
*/
 export const asString = ( val: any ) : string => {
    return val as string
 }

 /*
* for typecasting in template to satisfy TS nonsense
*/
export const asStringArray = ( val: Array<any> ) : Array<string> => {
  return val as Array<string>
}

/*
* check if in electron environment
*/
export const isElectron = () : boolean => {
      // Renderer process
      //@ts-ignore
    if (typeof window !== 'undefined' && typeof window.process === 'object' && window.process.type === 'renderer') {
        return true;
    }

    // Main process
    //@ts-ignore
    if (typeof process !== 'undefined' && typeof process.versions === 'object' && !!process.versions.electron) {
        return true;
    }

    // Detect the user agent when the `nodeIntegration` option is set to true
    if (typeof navigator === 'object' && typeof navigator.userAgent === 'string' && navigator.userAgent.indexOf('Electron') >= 0) {
        return true;
    }

    return false;
}

/*
* Get differences between two objects as new object
*/
export const diff = function (obj1, obj2, enforceTypes = false) {

    // Make sure an object to compare is provided
    if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
        return obj1;
    }

    //
    // Variables
    //

    var diffs = {};
    var key;


    //
    // Methods
    //

    /**
     * Check if two arrays are equal
     * @param  {Array}   arr1 The first array
     * @param  {Array}   arr2 The second array
     * @return {Boolean}      If true, both arrays are equal
     */
    var arraysMatch = function (arr1, arr2) {

        // Check if the arrays are the same length
        if (arr1.length !== arr2.length) return false;

        // Check if all items exist and are in the same order
        for (var i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }

        // Otherwise, return true
        return true;

    };

    /**
     * Compare two items and push non-matches to object
     * @param  {*}      item1 The first item
     * @param  {*}      item2 The second item
     * @param  {String} key   The key in our object
     */
    var compare = function (item1, item2, key) {

        // Get the object type
        var type1 = Object.prototype.toString.call(item1);
        var type2 = Object.prototype.toString.call(item2);

        // If type2 is undefined it has been removed
        if (type2 === '[object Undefined]') {
            diffs[key] = null;
            return;
        }

        // If items are different types
        if (enforceTypes && (type1 !== type2)) {
          console.devlog("checking types")
            diffs[key] = item2;
            return;
        }

        // If an object, compare recursively
        if (type1 === '[object Object]') {
            var objDiff = diff(item1, item2);
            if (Object.keys(objDiff).length > 0) {
                diffs[key] = objDiff;
            }
            return;
        }

        // If an array, compare
        if (type1 === '[object Array]') {
            if (!arraysMatch(item1, item2)) {
                diffs[key] = item2;
            }
            return;
        }

        // Else if it's a function, convert to a string and compare
        // Otherwise, just compare
        if (type1 === '[object Function]') {
            if (item1.toString() !== item2.toString()) {
                diffs[key] = item2;
            }
        } else {
          if(enforceTypes) {
            if (item1 !== item2 ) {
                diffs[key] = item2;
            }
          } else {
            if (item1 != item2 ) {
                diffs[key] = item2;
            }
          }
        }


    };


    //
    // Compare our objects
    //

    // Loop through the first object
    for (key in obj1) {
        if (obj1.hasOwnProperty(key)) {
            compare(obj1[key], obj2[key], key);
        }
    }

    // Loop through the second object and find missing items
    for (key in obj2) {
        if (obj2.hasOwnProperty(key)) {
            if (!obj1[key] && obj1[key] !== obj2[key] ) {
                diffs[key] = obj2[key];
            }
        }
    }


    var removeNullOrUndefined = function (obj) {
        return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));
      }

    // Return the object of differences
    return removeNullOrUndefined(diffs);

};

export const diffArray = function (newArr, oldArr) {
    return newArr.filter(x => !oldArr.includes(x));
}

export function comparer(otherArray){
    return function(current){
      return otherArray.filter(function(other){
        return other.value == current.value && other.display == current.display
      }).length == 0;
    }
  }


export const navigateToProfileById = (router: Router, profileId: string): void => {
    router.navigate(['/pages/profile', profileId])
};

export const readFileAsBase64 = async (file: File) : Promise<string | ArrayBuffer>  => {
    return new Promise<string | ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        return resolve((e.target as FileReader).result);
      };

      reader.onerror = e => {
        console.deverror(`FileReader failed on file ${file.name}.`);
        return reject(null);
      };

      if (!file) {
        console.deverror('No file to read.');
        return reject(null);
      }

      reader.readAsDataURL(file);
    });
  }

export const openUrl = (url: string) =>{
    window.open(url, '_blank');
}

export const base64toFile = (dataurl, filename) : File => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n) {
      u8arr[n - 1] = bstr.charCodeAt(n - 1)
      n -= 1 // to make eslint happy
    }
    return new File([u8arr], filename, { type: mime })
  }

export interface envOptions {
        production: boolean,
        childOrMasterOrGateway: 'child' | 'master' | 'gateway',
        apiUrl: string,
        apiUrlMaster: string,
        projectName: string,
        baseUrl: string,
        nameLabel: string, // a tag that can be also displayed as a name for an instance in the UI
        urlSuffix: './assets/i18n/', // needed by ngx-translate -> TranslateHttpLoader for locating translation files properly
        letterTemplateUrl?: string
        gatewayFrontend: string // url to the gateway frontend of that build
        environmentType: 'local' | 'dev' | 'prod' | 'test' // local is local, dev will be created on hetzner, prod does not exist of course, test is currently deployed on hetzner
        gcloudMapsPublicApiKey: string
}

export const filterRoleIdByName = (roles: IstrapiRole[], roleName: string) : number => {
    return roles.find(role => role.name.toLowerCase() === roleName.toLowerCase()).id;
}

/**
 * @param {FormGroup} formGroupRef - A reference to the FormGroup object utilised in the datatype using this function..
 * @param {FormBuilder} formBuilderRef - A reference to the form builder object utilised in the datatype using this function.
 * @param {StateService} stateRef - A reference to the state service utilised in the datatype using this function.
 * @param {entryProp[]} dataFields - Either just the data schema or the data schema with values to prefill the form, e.g. when editing the data  and data is already available.
 * @param {prefillFormWithValues} [prefillFormWithValues=false] - If the passed dataFields have a .value property defined (e.g. when editing existing data), prefill the forms with these values.
 * @returns { void }
 */
export const createForm = (formGroupRef : FormGroup, formBuilderRef : FormBuilder, stateRef : StateService, dataFields: entryProp[], prefillFormWithValues = false) =>{
    for (const dataField of dataFields) {
      const validatorsToAdd = [];
      const asyncValidatorsToAdd = [];

      // handle required:
      if (dataField.required) {
        validatorsToAdd.push(Validators.required);
      }

      // handle length
      if (dataField.minLength) {
        validatorsToAdd.push(Validators.minLength(dataField.minLength));
      }

      // handle twoFA
      if (dataField.name === "twoFA") {
        dataField.value = dataField.value.twoFAmethod;
      }

      // handle role
      if (dataField.name === "role") {
        asyncValidatorsToAdd.push(RoleValidator.asyncGetRoleExistsValidator(stateRef));
      }

      // add extra controls for date
      // 'visible' is defined by datepicker component
      if (dataField.type === "date") {
        formGroupRef.addControl(
          dataField.name + "-visible",
          formBuilderRef.control("", [Validators.required, DateValidator.validateDateFormat])
        );

      }

      // handle datatype-specific validators:
      switch (dataField.type) {
        case "email":
          validatorsToAdd.push(Validators.email);
          break;
        case "boolean":
           validatorsToAdd.push(BoolValidator.validateBool);
          break;
        case "password":
          validatorsToAdd.push(PasswordValidator.strong);
          break;
        case "matricule":
          // required-ness is handled above; an optional matricule may stay empty.
          validatorsToAdd.push(dataField.required ? MatriculeValidator.validateMatricule : MatriculeValidator.validateMatriculeOptional);
          break;
          case "dateBefore":
            formGroupRef.addControl(
              dataField.name + "-visible",
              formBuilderRef.control(prefillFormWithValues ? setDateIfDefined(dataField) : null, [Validators.required, DateValidator.validateDateBefore])
            );
            validatorsToAdd.push(DateValidator.validateDateFormat);
            validatorsToAdd.push(DateValidator.validateDateBefore);
          break;
          case "dateAfter":
            formGroupRef.addControl(
              dataField.name + "-visible",
              formBuilderRef.control(prefillFormWithValues ? setDateIfDefined(dataField) : null, [Validators.required, DateValidator.validateDateAfter])
            );
            validatorsToAdd.push(DateValidator.validateDateFormat);
            validatorsToAdd.push(DateValidator.validateDateAfter);
          break;
          case "dateAny":
            formGroupRef.addControl(
              dataField.name + "-visible",
              formBuilderRef.control(prefillFormWithValues ? setDateIfDefined(dataField) : null, [Validators.required, DateValidator.validateDateFormat])
            );
            validatorsToAdd.push(DateValidator.validateDateFormat);
          break;
        default:
          break;
      }
      formGroupRef.addControl(
        dataField.name,
        /* formBuilderRef.control("", validatorsToAdd) */
        formBuilderRef.control(prefillFormWithValues ? dataField.value : "" , validatorsToAdd, asyncValidatorsToAdd)
      );
    }
  }

  const setDateIfDefined = (datafield) => {
    if(datafield.value){
      return new Date(datafield.value)
    }
  }

  // resolves with the roles, fetching them first if they are not loaded yet (rejects if the request fails)
  export const awaitRolesLoaded = (stateRef : StateService) : Promise<IstrapiRole[]> => {
    return stateRef.loadRoles();
  }

  /**
   * This function is used to patch form data before creating an entry, e.g. formatting values based on their type, etc.
   * For temporary use when not utilising base classes.
   *
   * @param {FormGroup} form - The form group to patch.
   * @param {entryProp[] | indexDataField[]} schema - The schema of the data type.
   * */
export function patchFormDataBeforeCreate (form: FormGroup, schema: entryProp[] | indexDataField[]) {
        // 1. if the schema has entries of datatype number, we need to replace all commas with dots for the corrresponding field values in the form group.

        // 1.1 in the schema, find all fields that are of type number.
        let numberFields = schema.filter((field) => field.type == "number")

        // 1.2 for each field, replace all commas with dots in the corresponding form group field.
        numberFields.forEach((field) => {
          let formControl = form.controls[field.name]
          formControl.setValue(formControl.value.replace(/,/g, '.'))
        })
      }

  /**
   * This function is used to patch 'changes' before updating an entry, e.g. formatting values based on their type, etc.
   * For temporary use when not utilising base classes.
   *
   * @param {FormGroup} form - The form group to patch.
   * @param {entryProp[] | indexDataField[]} schema - The schema of the data type.
   * */
export function patchChangesDataBeforeUpdate (changes: object, schema: entryProp[] | indexDataField[]) {
   // 0. make a copy of changes
        let changesCopy = {...changes}


       // 1. if the schema has entries of datatype number, we need to replace all commas with dots for the corrresponding changes values

        // 1.1 in the schema, find all fields that are of type number.
        let numberFieldsNames = []
        schema.filter((field) => field.type == "number" ? numberFieldsNames.push(field.name) : null)

        // 1.2 for each changes field, replace all commas with dots in the corresponding form group field.

        let changesToTransform = []

        Object.keys(changes).forEach((key) => {
          if (numberFieldsNames.includes(key)) {
            changesToTransform.push(key)
          }
        })

        // 1.3 now replace the commas with dots in the found changes
        changesToTransform.forEach((field) => {
          changesCopy[field] = changesCopy[field].replace(/,/g, '.')
        })

        return changesCopy
      }


export const setDateToFormcontrolFromCalendar = (event, controlName, formGroupRef: FormGroup) =>{
    let day = dayjs(event);
    let correctFormField: AbstractControl = formGroupRef.controls[controlName];
    // set what to display in form field
    // set what value to put in form control, but don't emit event to view
    correctFormField.setValue(day.format("YYYY-MM-DD"));
  }

  // the default implementation of logForm.
  export const logForm = (form : FormGroup) =>{
    console.devlog('Form:', form)
  }

  // remove non numberic characters from a string except the dots
  const stripNonNumeric = (v: unknown): string =>
    String(v ?? '').replace(/[^\d.]/g, '');

// display a warning, if the frontend and backend types aren't matching.
  export const compareTypesVersionAndShowWarning = (backendVersion, toastrService, translate?: { instant: (key: string, params?: any) => string }) => {
    // let frontendVersion = '1.0.0';
    let frontendVersion = environment.typesVersion;

    if (stripNonNumeric(frontendVersion) === stripNonNumeric(backendVersion)){
      return;
    }

    let toastCfg : Partial<NbToastrConfig> = { status: ('danger' as NbComponentStatus), position: ('top-right' as NbGlobalPhysicalPosition), duration: 13000, /* icon: 'nb-locked',  */ /* toastClass: 'toastClassTest' */ };
    if (backendVersion !== frontendVersion) {
        console.devwarn(`Version mismatch detected between backend and frontend types! Backend version: ${backendVersion}, Frontend types version: ${frontendVersion}. This may lead to unexpected behavior.`);
        setTimeout(() => {
            const message = translate
              ? translate.instant('versionMismatch.message', { backend: backendVersion, frontend: frontendVersion })
              : `Backend: ${backendVersion}, Frontend: ${frontendVersion}`;
            const title = translate
              ? translate.instant('versionMismatch.title')
              : 'Types Package Version Mismatch!';
            toastrService.show(message, title, toastCfg);
        }, 500);
    }
}

@Injectable({
    providedIn: 'root'
  })
export class NavigationHelpers {

    environment = environment;
    constructor(public router: Router) {

    }

    toProfileById(profileId: string | number, editMode?: boolean, fetchFromMaster?: boolean): void {
        if(editMode && !fetchFromMaster) {
        this.router.navigate(['/pages/profile', profileId],
            {queryParams: { edit: true }}
        )} else if (!editMode && fetchFromMaster){
            this.router.navigate(['/pages/profile', profileId],
                {queryParams: { fetchFromMaster: true }}
            )
        } else {
            this.router.navigate(['/pages/profile', profileId])
        }
    }

    toTarifById(tarifId: string | number, editMode?: boolean, fetchFromMaster?: boolean): void {
        if(editMode && !fetchFromMaster) {

        this.router.navigateByUrl('/',{skipLocationChange:true}).then(()=>{
            this.router.navigate(['/pages/tarifs', tarifId], {
                queryParams: { edit: true },
              } )
          })


    } else if (!editMode && fetchFromMaster){
            this.router.navigate(['/pages/tarifs', tarifId],
                {queryParams: { fetchFromMaster: true }}
            )
        } else {
            this.router.navigate(['/pages/tarifs', tarifId])
        }
    }

    toSuffixById(suffixId: string | number, editMode?: boolean, fetchFromMaster?: boolean): void {
        if(editMode && !fetchFromMaster) {
        this.router.navigate(['/pages/suffixes', suffixId],
            {queryParams: { edit: true }}
        )} else if (!editMode && fetchFromMaster){
            this.router.navigate(['/pages/suffixes', suffixId],
                {queryParams: { fetchFromMaster: true }}
            )
        } else {
            this.router.navigate(['/pages/suffixes', suffixId])
        }
    }

    toOwnProfile(): void {
        this.router.navigateByUrl('/pages/profile/me', { skipLocationChange: false });
    }

    toLoginPage(): void {
        this.router.navigate(['/auth/login'])
    }

    linkToProfileById(profileId: string | number): string {
        return environment.baseUrl + 'pages/profile/' + profileId;
    }

    linkToTarifById(tarifId: string | number): string {
        return environment.baseUrl + 'pages/tarifs/' + tarifId;
    }

    linkToSuffixById(suffixId: string | number): string {
        return environment.baseUrl + 'pages/suffixes/' + suffixId;
    }

    toSearchPage(term? : string): void {
        if (term) {
            this.router.navigate(['/pages/search'], {queryParams: { q: term }})
        } else {
            this.router.navigate(['/pages/search'])
        }
    }

    toTarifSearchPage(term? : string): void {
        if (term) {
            this.router.navigate(['/pages/tarifs/search'], {queryParams: { q: term }})
        } else {
            this.router.navigate(['/pages/tarifs/search'])
        }
    }

    toSuffixSearchPage(term? : string): void {
        if (term) {
            this.router.navigate(['/pages/suffixes/search'], {queryParams: { q: term }})
        } else {
            this.router.navigate(['/pages/suffixes/search'])
        }
    }

    toIndexById(indexId: string | number, editMode?: boolean, fetchFromMaster?: boolean): void {
        if(editMode && !fetchFromMaster) {
        this.router.navigate(['/pages/indices', indexId],
            {queryParams: { edit: true }}
        )} else if (!editMode && fetchFromMaster){
            this.router.navigate(['/pages/indices', indexId],
                {queryParams: { fetchFromMaster: true }}
            )
        } else {
            this.router.navigate(['/pages/indices', indexId])
        }
    }

    linkToIndexById(indexId: string | number): string {
        return environment.baseUrl + 'pages/indices/' + indexId;
    }

    toIndexSearchPage(term? : string): void {
        if (term) {
            this.router.navigate(['/pages/indices/search'], {queryParams: { q: term }})
        } else {
            this.router.navigate(['/pages/indices/search'])
        }
    }

}


/**
 * Wraps an API function so concurrent callers with the same args share one in-flight
 * request, and resolved values are cached for `debounceSecs`. Different args produce
 * independent in-flight promises and cache entries (keyed via `keyFn`, default JSON.stringify).
 *
 * @param apiFn        The API function to wrap.
 * @param debounceSecs How long a resolved result stays cached before a new call is allowed.
 * @param uniqueCacheIdFunction        Optional. Builds cache/dedup key from args. Default: JSON.stringify(args).
 */
export function makeDebouncedApi<T, A extends any[]>(
  apiFn: (...args: A) => Promise<T>,
  debounceSecs: number,
  uniqueCacheIdFunction: (...args: A) => string = (...args) => JSON.stringify(args),
): (...args: A) => Promise<T> {
  const inflight = new Map<string, Promise<T>>();
  const cache = new Map<string, { value: T; expiresAt: number }>();
  const ttlMs = debounceSecs * 1000;

  return (...args: A): Promise<T> => {
    const key = uniqueCacheIdFunction(...args);

    const hit = cache.get(key);
    if (hit && Date.now() < hit.expiresAt) {
      return Promise.resolve(hit.value);
    }

    const pending = inflight.get(key);
    if (pending) return pending;

    const p = apiFn(...args)
      .then(value => {
        cache.set(key, { value, expiresAt: Date.now() + ttlMs });
        return value;
      })
      .finally(() => {
        inflight.delete(key);
      });

    inflight.set(key, p);
    return p;
  };
}

   /*  debounceAndReturnOnceDefinedGenerator<T>(apiFn: () => T | undefined, debounce: number, valueGeter: () => T | undefined, valueSetter: () => T | undefined): () => T | undefined {

      return (valueGeter: () => T | undefined) => {
        let currentlyWaiting = false;
        var result: T | undefined;
        if (!currentlyWaiting) {
          currentlyWaiting = true;

        } else {
          if (valueGeter() !== undefined) {
            return valueGeter();
          } else {}

        }

      }
    } */

interface IErrorBase<T> {
   error: Error | AxiosError<T>;
   type: 'axios-error' | 'stock-error';
}

interface IAxiosError<T> extends IErrorBase<T> {
   error: AxiosError<T>;
   type: 'axios-error';
}
interface IStockError<T> extends IErrorBase<T> {
   error: Error;
   type: 'stock-error';
}

// makes errorType available in axios error, see https://github.com/axios/axios/issues/3612#issuecomment-893017210
export function axiosErrorHandler<T>(
   callback: (err: IAxiosError<T> | IStockError<T>) => void
) {
   return (error: Error | AxiosError<T>) => {
      if (axios.isAxiosError(error)) {
         callback({
            error: error,
            type: 'axios-error'
         });
      } else {
         callback({
            error: error,
            type: 'stock-error'
         });
      }
   };
}

// this logic handles chunk-loading errors (because of bad caching of the index.html etc.) by reloading the page once, 
// if such an error occurs, and aborts, if the last reload because of a chunk load error was more than 30 seconds ago, 
// to avoid infinite reload loops. 
// 
// This is needed because otherwise the only way to get back to a working state would be to clear the cache or do a hard reload, which is bad for user experience. With this logic, in most cases simply reloading the page once will be sufficient to fix the issue when it occurs, without the user having to do anything special.

const CHUNK_RELOAD_WINDOW_MS = 30_000;
const CHUNK_RELOAD_STORAGE_KEY = 'chunk-reload-ts';

export function isChunkLoadError(error: any): boolean {
  const msg = error?.message ?? '';
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
}

export function reloadForChunkError(): boolean {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
  if (Date.now() - last <= CHUNK_RELOAD_WINDOW_MS) {
    return false;
  }
  sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
  const url = new URL(window.location.href);
  url.searchParams.set('_chunkReload', String(Date.now()));
  window.location.href = url.toString();
  return true;
}

export class ChunkLoadErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    if (isChunkLoadError(error) && reloadForChunkError()) {
      return;
    }
    console.deverror(error);
  }
}

// chunk load error handling logic end
/////////

export const copyToClipboard = async (text: string | undefined | null): Promise<void> => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }